import { useCallback } from 'react'

import { useUnit } from 'effector-react'

import { Page } from '@/components/layout/page/page'
import { usePaginationState } from '@/components/ui/table/hooks/usePaginationState'
import { useSearchQuery } from '@/components/ui/table/hooks/useSearchQuery'
import { DIALOG_TYPE } from '@/dialogs/constants'
import { $creatorId } from '@/models/creators.ts'
import { openDialog } from '@/models/dialogs'
import { RecentSpenderListTable } from '@/modules/creator-settings/lists/recent-spenders/components/RecentSpenderListTable'
import { RecentSpendersHeaderButtons } from '@/modules/creator-settings/lists/recent-spenders/components/RecentSpendersHeaderButtons'
import { useGetRecentSpendLists } from '@/modules/creator-settings/lists/recent-spenders/hooks/useGetRecentSpendLists.ts'
import { useRecentSpendListMutations } from '@/modules/creator-settings/lists/recent-spenders/hooks/useRecentSpendListMutations'
import { type RecentSpendListFormValues } from '@/modules/creator-settings/lists/recent-spenders/types'

export const RecentSpendersView = () => {
  const creatorId = useUnit($creatorId)
  const { paginationState, setPaginationState } = usePaginationState({
    pageIndex: 0,
    pageSize: 10,
  })
  const { searchQuery, setSearchQuery } = useSearchQuery('')

  const { data, isFetching } = useGetRecentSpendLists(
    creatorId ?? 0,
    paginationState,
    searchQuery,
  )
  const { createRecentSpendList } = useRecentSpendListMutations(creatorId ?? 0)

  const handleCreate = useCallback(
    async (values: RecentSpendListFormValues) => {
      return createRecentSpendList.mutateAsync({
        name: values.name,
        minutes: values.minimumMinutes,
      })
    },
    [createRecentSpendList],
  )

  const handleCreateList = useCallback(() => {
    openDialog({
      type: DIALOG_TYPE.RECENT_SPENDER_LIST_FORM,
      onSubmit: handleCreate,
      title: 'Create new Recent Spend List',
      submitLabel: 'Create',
    })
  }, [handleCreate])

  return (
    <Page
      title="Recent Spend Lists"
      description="See the list of fans who recently spent on the chosen creator account"
      headerOptions={
        <RecentSpendersHeaderButtons
          onNewListPress={handleCreateList}
          setFilter={setSearchQuery}
        />
      }
    >
      <RecentSpenderListTable
        list={data?.content ?? []}
        isFetching={isFetching}
        paginationState={paginationState}
        onPaginationChange={setPaginationState}
        pageCount={data?.totalPages ?? 0}
      />
    </Page>
  )
}
